import { notification } from 'antd';
import * as Sentry from '@sentry/react';

import { ERROR_HANDLING_CONFIG } from '../../config/errorHandlingConfig';
import packageJson from '../../../package.json';
import type { AppErrorCodeEnum } from '../../enums/AppErrorCodeEnum';
import type { AppState } from '../../store/store';
import { useAppStore } from '../../store/store';
import i18n from '../i18n';

/**
 * Correlation ID for the current session.
 * Used to correlate frontend logs/errors with backend logs.
 * The ID is unique per browser tab/session, created once and reused.
 */
let sessionCorrelationId: string | null = null;

/**
 * Returns the current correlation ID, generating one if it does not exist yet.
 * Falls back to Date+random if crypto.randomUUID is unavailable.
 */
function getOrCreateCorrelationId(): string {
  if (sessionCorrelationId != null) return sessionCorrelationId;
  try {
    sessionCorrelationId = crypto.randomUUID();
  } catch {
    sessionCorrelationId = `fn-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
  return sessionCorrelationId;
}

/**
 * Options to customize the error handling behavior.
 */
export interface HandleErrorOptions {
  /** Override or add i18n interpolation params (e.g. { minutes: 5 }, { resource: 'common.files' }) */
  i18nParams?: Record<string, string | number>;
  /** Optional description for the notification */
  description?: string;
  /** Override whether to log this occurrence to Sentry */
  logToSentry?: boolean;
  /** Notification duration in seconds */
  duration?: number;
  /** Raw error to attach to Sentry (stack trace, response, etc.) */
  error?: unknown;
  /** Callback after showing the notification (e.g. redirect) */
  onNotify?: () => void;
}

/**
 * Regexp to determine if a string looks like an i18n key (e.g. "common.files", "errors.sessionExpired").
 */
const I18N_KEY_PATTERN = /^[\w.]+\w$/;

/**
 * Resolves provided i18nParams, translating values that look like i18n keys.
 * Translates string values matching I18N_KEY_PATTERN; all others are left as-is.
 */
function resolveI18nParams(
  params: Record<string, string | number> | undefined,
): Record<string, string | number> {
  if (!params || Object.keys(params).length === 0) return {};
  const resolved: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    // If the value is a string and matches the i18n key pattern, translate it; otherwise use as-is
    resolved[k] = typeof v === 'string' && I18N_KEY_PATTERN.test(v) ? (i18n.t(v) as string) : v;
  }
  return resolved;
}

/**
 * Centralized error handler for the application.
 *
 * Shows an Ant Design notification to the user based on error code config
 * and optionally logs the error to Sentry with context.
 * Use this instead of notification.error/warning/info directly to ensure consistency.
 *
 * @param code App-specific error code for lookup in error config
 * @param options Controls notification params, interpolation, Sentry logging, etc.
 */
export function handleError(code: AppErrorCodeEnum, options: HandleErrorOptions = {}): void {
  // Obtain config for this error code from central definition
  const config = ERROR_HANDLING_CONFIG[code];
  if (!config) {
    // Warn in dev if implementation/config got out of sync
    if (import.meta.env.DEV) {
      console.warn(`errorsHandler: no config for code ${code}`);
    }
    return;
  }

  // Destructure and apply defaults from incoming options
  const {
    i18nParams = {},
    description,
    logToSentry: logToSentryOverride,
    duration,
    error: rawError,
    onNotify,
  } = options;

  // Merge default param interpolations with user override
  const mergedParams = { ...config.defaultI18nParams, ...i18nParams };
  const resolvedParams = resolveI18nParams(mergedParams);

  // Compose the i18n error message
  const message = i18n.t(config.i18nKey, resolvedParams);

  // Determine Sentry logging policy: allow override, else use config default
  const shouldLogToSentry = logToSentryOverride ?? config.logToSentryByDefault;

  // Prepare payload for AntDesign notification API
  const notificationPayload = {
    message,
    ...(description != null && { description }),
    ...(duration != null && { duration }),
    placement: 'topRight' as const,
  };

  // Display the notification using the configured variant
  if (config.notificationType === 'error') {
    notification.error(notificationPayload);
  } else if (config.notificationType === 'warning') {
    notification.warning(notificationPayload);
  } else {
    notification[config.notificationType](notificationPayload);
  }

  // Optionally send error information to Sentry for tracing
  if (shouldLogToSentry) {
    // Grab current app state (for user/audit metadata)
    const state = useAppStore.getState() as AppState;
    const authenticatedUser = state?.authenticatedUser ?? null;

    Sentry.withScope((scope) => {
      // Tag and fingerprint for grouping in Sentry dashboard
      scope.setTag('error_code', code);
      scope.setTag('correlation_id', getOrCreateCorrelationId());
      scope.setExtra('app_version', packageJson.version);
      scope.setExtra('build_id', typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : '');
      scope.setExtra('audit_user_id', authenticatedUser?.id ?? null);
      scope.setExtra('audit_location', typeof window !== 'undefined' ? window.location.href : '');
      scope.setFingerprint([code]);
      // Attach context and stack information
      if (rawError instanceof Error) {
        scope.setExtra('originalError', rawError.message);
        Sentry.captureException(rawError); // capture stack trace if possible
      } else {
        if (rawError != null) scope.setExtra('context', rawError);
        Sentry.captureMessage(message, 'error'); // generic text if not an Error instance
      }
    });
  }

  // Invoke the optional callback after the notification (e.g. for redirect)
  onNotify?.();
}
